{
  "blocks": [
    {
      "id": "test-pgt-m",
      "kind": "test",
      "tags": [
        "testing",
        "both-partners",
        "genetic-screening",
        "ivf",
        "genomic"
      ],
      "title": "PGT-M (Monogenic Disorder Screening)",
      "subtitle": "Tests embryos for a specific known single-gene mutation",
      "icon": "🧬",
      "body": {
        "testName": "Preimplantation Genetic Testing for Monogenic Disorders (PGT-M)",
        "category": "genetic",
        "parentTest": {
          "testId": "test-preimplantation-genetic-testing",
          "badgeLabel": "View all PGT tests"
        },
        "evidenceStrength": "strong",
        "whatItMeasures": "Tests embryos for a specific known single-gene condition or familial variant, such as cystic fibrosis, sickle cell disease, Huntington disease, or a hereditary cancer predisposition variant.\n\nUnlike PGT-A, which checks chromosome number, PGT-M is targeted to a defined genetic question identified through carrier screening, family history, or diagnostic genetic testing.",
        "howItWorks": "PGT-M usually requires a custom test work-up before the IVF cycle or before embryo testing. The lab uses DNA from the intended parents and sometimes relatives to build a way to track the familial variant or inherited chromosome segment. Embryo biopsy cells are then tested, often with PGT-A from the same biopsy if the clinic and lab offer combined testing.",
        "howItWorksSteps": [
          "A genetic counselor or genetics lab confirms the exact familial variant and inheritance pattern.",
          "The lab builds or validates a custom PGT-M work-up using DNA from the intended parents and sometimes relatives.",
          "Embryos are created through IVF and biopsied at the blastocyst stage.",
          "The lab tests embryo biopsy DNA for the familial variant or linked markers.",
          "The report classifies embryos as unaffected, carrier, affected or high-risk, or no-result depending on the condition and lab method."
        ],
        "quickFacts": [
          {
            "kind": "sample",
            "value": "Embryo biopsy plus family DNA"
          },
          {
            "kind": "time",
            "value": "No extra patient procedure"
          },
          {
            "kind": "pain",
            "value": "None for patient"
          },
          {
            "kind": "anesthesia",
            "value": "None for the biopsy"
          },
          {
            "kind": "prep",
            "value": "Custom work-up before testing"
          },
          {
            "kind": "results",
            "value": "1-2 weeks after biopsy"
          }
        ],
        "resultTypes": [
          {
            "label": "Unaffected",
            "meaning": "The embryo does not appear to carry the disease-causing variant being tested.",
            "whatToExpect": "Clinics usually prioritize unaffected embryos, often alongside PGT-A or morphology information if available."
          },
          {
            "label": "Carrier",
            "meaning": "For recessive conditions, the embryo may carry one copy of the variant but is not expected to be affected by that condition.",
            "whatToExpect": "Transfer decisions depend on the condition, inheritance pattern, clinic policy, and whether unaffected non-carrier embryos are available."
          },
          {
            "label": "Affected or high-risk",
            "meaning": "The embryo appears to carry the variant pattern associated with the condition being tested.",
            "whatToExpect": "Most clinics do not recommend transfer, but decisions should be reviewed with genetic counseling because inheritance patterns and severity vary."
          },
          {
            "label": "No-result",
            "meaning": "The lab cannot make a reliable call for that embryo.",
            "whatToExpect": "Your clinic may discuss re-biopsy, transfer without a result, or deprioritizing the embryo depending on embryo quality and alternatives."
          }
        ],
        "expectations": {
          "canExpect": [
            "A targeted embryo result for the specific familial condition being tested",
            "Pre-test genetic counseling about inheritance, residual risk, and what each result category means",
            "Possible combined PGT-A and PGT-M from one embryo biopsy, depending on lab workflow"
          ],
          "shouldNotExpect": [
            "A broad screen for every genetic condition",
            "A guarantee of implantation, pregnancy, or a child without health conditions",
            "A useful result if the familial variant, inheritance pattern, or custom work-up cannot be validated"
          ]
        },
        "considerations": [
          "Ask whether the condition is autosomal recessive, autosomal dominant, X-linked, mitochondrial, adult-onset, reduced penetrance, or a variant of uncertain significance.",
          "Ask whether relatives need to provide DNA for the work-up and how long the lab needs before IVF or biopsy.",
          "Ask whether carrier embryos are considered transferable for recessive conditions.",
          "Ask how the clinic handles no-result embryos and whether prenatal diagnostic testing is still recommended after pregnancy."
        ],
        "bestFor": [
          "Known high-risk single-gene condition in one or both genetic parents",
          "Carrier couples at risk of having an affected child with the same recessive condition",
          "Autosomal dominant or X-linked conditions where an embryo may inherit a familial variant",
          "Some adult-onset or reduced-penetrance conditions after careful counseling"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "ASRM says PGT-M is available for most Mendelian conditions when a significant reproductive risk is identified",
            "mechanism": "The test targets a known familial variant or linked markers rather than estimating broad risk.",
            "source": "ASRM PGT-M committee opinion, 2023"
          },
          {
            "direction": "neutral",
            "outcome": "PGT-M can often be performed with PGT-A from a single biopsy, but labs differ in workflow and cost",
            "source": "ASRM PGT-M committee opinion, 2023"
          },
          {
            "direction": "neutral",
            "outcome": "Some indications require special counseling, including adult-onset conditions, reduced penetrance, HLA matching, or variants with uncertain meaning",
            "source": "ASRM PGT-M committee opinion, 2023"
          }
        ],
        "costRange": {
          "low": 3000,
          "high": 8000
        },
        "atHome": false,
        "clinicRequired": false,
        "dataPoints": [],
        "section": "genetic-screening"
      },
      "sources": [
        {
          "title": "Indications and management of preimplantation genetic testing for monogenic conditions: a committee opinion",
          "authors": "Practice Committee of the American Society for Reproductive Medicine",
          "journal": "Fertility and Sterility",
          "year": 2023,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/indications-and-management-of-preimplantation-genetic-testing-for-monogenic-conditions-a-committee-opinion-2023/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "ESHRE PGT Consortium good practice recommendations for the detection of monogenic disorders",
          "authors": "Carvalho F, Moutou C, Dimitriadou E, et al.",
          "journal": "Human Reproduction Open",
          "year": 2020,
          "url": "https://academic.oup.com/hropen/article/2020/3/hoaa018/5848305",
          "evidenceLevel": "guideline"
        }
      ],
      "readTimeMinutes": 5
    }
  ],
  "relationships": [],
  "localeVariants": []
}
